import { useState, useCallback } from 'react';
import axios from 'axios';
import { COMPLIANCE_ENDPOINTS } from '../constants/api';
import type { ListingSummary } from '../types/ListingTypes';

export interface ComplianceAgency {
  id: string;
  company_name: string;
  trading_as_name: string | null;
  archived: boolean;
}

export interface ComplianceDocumentFolder {
  path: string[];
  documents: Array<{ id: string; name: string; url: string; createdAt: string; expiryDate: string | null }>;
}

export interface ComplianceDocumentMap {
  folders: ComplianceDocumentFolder[];
  totalDocuments: number;
  userCount: number;
}

const getComplianceHeaders = () => {
  const token = sessionStorage.getItem('complianceToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const useComplianceData = () => {
  const [agencies, setAgencies] = useState<ComplianceAgency[]>([]);
  const [agenciesLoading, setAgenciesLoading] = useState(false);

  const [listings, setListings] = useState<ListingSummary[]>([]);
  const [listingsLoading, setListingsLoading] = useState(false);

  const [documentMap, setDocumentMap] = useState<ComplianceDocumentMap | null>(null);
  const [documentMapLoading, setDocumentMapLoading] = useState(false);

  const fetchAgencies = useCallback(async () => {
    setAgenciesLoading(true);
    try {
      const { data } = await axios.get(COMPLIANCE_ENDPOINTS.AGENCIES, { headers: getComplianceHeaders() });
      setAgencies(data.agencies || []);
    } finally {
      setAgenciesLoading(false);
    }
  }, []);

  const fetchAgencyListings = useCallback(async (agencyId: string): Promise<ListingSummary[]> => {
    setListingsLoading(true);
    try {
      const { data } = await axios.get(COMPLIANCE_ENDPOINTS.AGENCY_LISTINGS(agencyId), { headers: getComplianceHeaders() });
      const fetched = data.listings || [];
      setListings(fetched);
      return fetched;
    } finally {
      setListingsLoading(false);
    }
  }, []);

  const fetchAgencyDocumentMap = useCallback(async (agencyId: string): Promise<ComplianceDocumentMap> => {
    setDocumentMapLoading(true);
    try {
      const { data } = await axios.get(COMPLIANCE_ENDPOINTS.AGENCY_DOCUMENT_MAP(agencyId), { headers: getComplianceHeaders() });
      setDocumentMap(data);
      return data as ComplianceDocumentMap;
    } finally {
      setDocumentMapLoading(false);
    }
  }, []);

  const downloadAgencyDocumentMap = useCallback(async (agencyId: string, agencyName: string) => {
    const response = await axios.get(COMPLIANCE_ENDPOINTS.AGENCY_DOCUMENT_MAP_EXPORT(agencyId), {
      headers: getComplianceHeaders(),
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${agencyName}-documents.zip`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }, []);

  return {
    agencies, agenciesLoading, fetchAgencies,
    listings, listingsLoading, fetchAgencyListings,
    documentMap, documentMapLoading, fetchAgencyDocumentMap,
    downloadAgencyDocumentMap,
  };
};
